import { useModal } from "shared/composables";

import { getCollectionDefault } from "~/common/defaults/collection.default";
import { getColorDefault } from "~/common/defaults/color.default";
import { getCustomerDefault } from "~/common/defaults/customer.default";
import { getItemAdaptedDefault, getItemGroupDefault } from "~/common/defaults/item.default";
import { getItemPrototypeAdaptedDefault } from "~/common/defaults/item-prototype.default";
import { getKindDefault } from "~/common/defaults/kind.default";
import { getMaterialDefault } from "~/common/defaults/material.default";
import { getOrderAdaptedDefault } from "~/common/defaults/order.default";
import { getPricePolicyDefault } from "~/common/defaults/price-policy.default";
import { getShippingDefault } from "~/common/defaults/shipping.default";
import { getSizeDefault } from "~/common/defaults/size.default";
import { getTagDefault } from "~/common/defaults/tag.default";
import { getUserDefault } from "~/common/defaults/user.default";
import { getUserSettingsDefault } from "~/common/defaults/user-settings.default";
import type { CollectionAdapted } from "~/common/interfaces/entities/collection.interface";
import type { ColorAdapted } from "~/common/interfaces/entities/color.interface";
import type { Customer } from "~/common/interfaces/entities/customer.interface";
import type { ItemAdapted, ItemGroup } from "~/common/interfaces/entities/item.interface";
import type { ItemPrototypeAdapted } from "~/common/interfaces/entities/item-prototype.interface";
import type { KindAdapted } from "~/common/interfaces/entities/kind.interface";
import type { MaterialAdapted } from "~/common/interfaces/entities/material.interface";
import type { OrderAdapted } from "~/common/interfaces/entities/order.interface";
import type { PricePolicy } from "~/common/interfaces/entities/price-policy.interface";
import type { Shipping } from "~/common/interfaces/entities/shipping.interface";
import type { Size } from "~/common/interfaces/entities/size.interface";
import type { Tag } from "~/common/interfaces/entities/tag.interface";
import type { User } from "~/common/interfaces/entities/user.interface";
import type { UserSettings } from "~/common/interfaces/entities/user-settings.interface";
;

export const useModalStore = defineStore("modal", () => {
	/** Orders */
	const ordersConfirmReject = {
		...useModal(),
		order: ref<OrderAdapted>(getOrderAdaptedDefault())
	};
	const ordersConfirmClose = {
		...useModal(),
		order: ref<OrderAdapted>(getOrderAdaptedDefault())
	};
	const ordersConfirmRefund = {
		...useModal(),
		order: ref<OrderAdapted>(getOrderAdaptedDefault())
	};

	/** Shippings */
	const shippingsModalEdit = {
		...useModal(),
		shipping: ref<Shipping>(getShippingDefault())
	};

	/** Products */
	const itemPrototypesConfirmDelete = {
		...useModal(),
		itemPrototype: ref<ItemPrototypeAdapted>(getItemPrototypeAdaptedDefault())
	};
	const itemsConfirmDelete = {
		...useModal(),
		item: ref<ItemAdapted>(getItemAdaptedDefault())
	};
	const itemsConfirmDeleteMultiple = {
		...useModal(),
		itemGroup: ref<ItemGroup>(getItemGroupDefault())
	};
	const tagsModalEdit = {
		...useModal(),
		tag: ref<Tag>(getTagDefault())
	};
	const tagsConfirmDelete = {
		...useModal(),
		tag: ref<Tag>(getTagDefault())
	};
	const collectionsModalEdit = {
		...useModal(),
		collection: ref<CollectionAdapted>(getCollectionDefault())
	};
	const collectionsConfirmDelete = {
		...useModal(),
		collection: ref<CollectionAdapted>(getCollectionDefault())
	};

	/** Components */
	const kindsModalEdit = {
		...useModal(),
		kind: ref<KindAdapted>(getKindDefault())
	};
	const kindsConfirmDelete = {
		...useModal(),
		kind: ref<KindAdapted>(getKindDefault())
	};
	const colorsModalEdit = {
		...useModal(),
		color: ref<ColorAdapted>(getColorDefault())
	};
	const colorsConfirmDelete = {
		...useModal(),
		color: ref<ColorAdapted>(getColorDefault())
	};
	const sizesModalEdit = {
		...useModal(),
		size: ref<Size>(getSizeDefault())
	};
	const sizesConfirmDelete = {
		...useModal(),
		size: ref<Size>(getSizeDefault())
	};
	const materialsConfirmDelete = {
		...useModal(),
		material: ref<MaterialAdapted>(getMaterialDefault())
	};

	/** Customers */
	const customersModalEdit = {
		...useModal(),
		customer: ref<Customer>(getCustomerDefault())
	};

	/** Price Policies */
	const pricePoliciesConfirmDelete = {
		...useModal(),
		pricePolicy: ref<PricePolicy>(getPricePolicyDefault())
	};

	/** Settings */
	const teamModalEdit = {
		...useModal(),
		user: ref<User>(getUserDefault())
	};
	const teamModalInvite = useModal();
	const profileModalEdit = {
		...useModal(),
		userSettings: ref<UserSettings>(getUserSettingsDefault())
	};

	return {
		ordersConfirmReject,
		ordersConfirmClose,
		ordersConfirmRefund,
		kindsModalEdit,
		kindsConfirmDelete,
		colorsModalEdit,
		colorsConfirmDelete,
		sizesModalEdit,
		sizesConfirmDelete,
		materialsConfirmDelete,
		itemPrototypesConfirmDelete,
		itemsConfirmDelete,
		itemsConfirmDeleteMultiple,
		tagsModalEdit,
		tagsConfirmDelete,
		collectionsModalEdit,
		collectionsConfirmDelete,
		customersModalEdit,
		pricePoliciesConfirmDelete,
		teamModalEdit,
		teamModalInvite,
		shippingsModalEdit,
		profileModalEdit
	};
});
